import { useEffect, useRef } from 'react';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

import { useGetProductColorsMoqMutation } from '@/api/ProductInfoQuery';
import {
  areStoreCartItemsReadyForMoqFetch,
  getStoreCartDesignGroupMoqRequestKey,
  fetchStoreCartDesignGroupsMoq,
  mapMoqResultsToUiDesignGroupKeys,
} from '@/modules/Cart/StoreCart/helpers/storeCartApiSync.utils';
import {
  buildStoreCartMoqDesignGroupMap,
  getStoreCartDesignGroupKey,
} from '@/modules/Cart/StoreCart/helpers/storeCartDesignGroups.utils';
import { buildStoreCartDesignGroupMoqStatuses } from '@/modules/Cart/StoreCart/helpers/storeCartSetup.utils';
import { STORE_CART_MOQ_FETCH_DEBOUNCE_MS } from '@/modules/Cart/StoreCart/storeCart.constants';
import {
  clearStoreCartDesignGroupMoqs,
  clearDesignGroupMoqFetchState,
  setDesignGroupMoqFetchComplete,
  setDesignGroupMoqFetchLoading,
  setDesignGroupMoqStatuses,
  setStoreCartDesignGroupMoqs,
} from '@/store/reducers/storeCart';
import { useAppDispatch } from '@/store/hooks';
import type { StoreCartMoqShippingLane } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';
import {
  DEFAULT_STORE_CART_MOQ_SHIPPING_LANE,
  normalizeStoreCartMoqShippingLane,
} from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';

export interface IUseStoreCartDesignGroupMoqsOptions {
  shippingLane?: StoreCartMoqShippingLane;
}

const setDesignGroupsMoqLoading = (
  dispatch: ReturnType<typeof useAppDispatch>,
  cartItems: ICartItemDetail[]
): void => {
  const loadingStatuses = Object.fromEntries(
    Array.from(new Set(cartItems.map((item) => getStoreCartDesignGroupKey(item)))).map(
      (groupKey) => [groupKey, 'loading' as const]
    )
  );

  dispatch(setDesignGroupMoqStatuses(loadingStatuses));
};

export const useStoreCartDesignGroupMoqs = (
  cartItems: ICartItemDetail[],
  options: IUseStoreCartDesignGroupMoqsOptions = {}
): void => {
  const dispatch = useAppDispatch();
  const shippingLane = normalizeStoreCartMoqShippingLane(
    options.shippingLane ?? DEFAULT_STORE_CART_MOQ_SHIPPING_LANE
  );
  const [getProductColorsMoq] = useGetProductColorsMoqMutation();
  const getProductColorsMoqRef = useRef(getProductColorsMoq);
  getProductColorsMoqRef.current = getProductColorsMoq;
  const requestIdRef = useRef(0);
  const lastFetchedMoqKeyRef = useRef('');
  const cartItemsRef = useRef(cartItems);
  cartItemsRef.current = cartItems;
  const moqRequestKey =
    cartItems.length && areStoreCartItemsReadyForMoqFetch(cartItems)
      ? getStoreCartDesignGroupMoqRequestKey(cartItems, shippingLane)
      : '';

  useEffect(() => {
    let debounceTimer: number | undefined;

    if (!moqRequestKey) {
      lastFetchedMoqKeyRef.current = '';
      if (!cartItemsRef.current.length) {
        dispatch(clearStoreCartDesignGroupMoqs());
        dispatch(clearDesignGroupMoqFetchState());
      }
    } else if (lastFetchedMoqKeyRef.current !== moqRequestKey) {
      dispatch(setDesignGroupMoqFetchLoading(moqRequestKey));
      setDesignGroupsMoqLoading(dispatch, cartItemsRef.current);

      debounceTimer = window.setTimeout(() => {
        if (lastFetchedMoqKeyRef.current === moqRequestKey) {
          return;
        }

        lastFetchedMoqKeyRef.current = moqRequestKey;

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        const designGroups = buildStoreCartMoqDesignGroupMap(cartItemsRef.current);

        const loadDesignGroupMoqs = async () => {
          let uiMoqs: Record<string, number> = {};

          try {
            const moqs = await fetchStoreCartDesignGroupsMoq(
              designGroups,
              async (request) => {
                const response = await getProductColorsMoqRef.current(request);

                if ('data' in response && response.data) {
                  return response.data;
                }

                return null;
              },
              shippingLane
            );

            if (requestIdRef.current !== requestId) {
              return;
            }

            uiMoqs = mapMoqResultsToUiDesignGroupKeys(moqs, cartItemsRef.current);
            dispatch(setStoreCartDesignGroupMoqs(uiMoqs));
          } catch {
            if (requestIdRef.current !== requestId) {
              return;
            }

            // MOQ fetch failed — use 0 so UI shows default breakpoint tiers
            uiMoqs = Object.fromEntries(
              Array.from(
                new Set(cartItemsRef.current.map((item) => getStoreCartDesignGroupKey(item)))
              ).map((groupKey) => [groupKey, 0])
            );
            dispatch(setStoreCartDesignGroupMoqs(uiMoqs));
          } finally {
            if (requestIdRef.current === requestId) {
              dispatch(
                setDesignGroupMoqStatuses(
                  buildStoreCartDesignGroupMoqStatuses(cartItemsRef.current, uiMoqs)
                )
              );
              dispatch(setDesignGroupMoqFetchComplete(moqRequestKey));
            }
          }
        };

        loadDesignGroupMoqs().catch(() => undefined);
      }, STORE_CART_MOQ_FETCH_DEBOUNCE_MS);
    }

    return () => {
      if (debounceTimer !== undefined) {
        window.clearTimeout(debounceTimer);
      }
    };
  }, [dispatch, moqRequestKey, shippingLane]);
};
